<template lang="pug">
  .panel-page
    .panel.mx-auto.body-1
      FormErrorAlert(
        v-show="alertMessage"
        :message="alertMessage"
        :alert-type="alertType"
        :heading="alertType === 'danger' ? 'Error' : 'Success'"
        @dismiss="alertMessage = ''"
      )
      h1 Add New User
      p Fill in the following information for the user you would like to add.
      FormHeader(title="PERSONAL INFO" required)
      form.form(
        @submit.prevent="submit" autocomplete="off"
      )
        .form-row
          .col-4
            .form-group
              label(for="username" required) User Name
              input#username.form-control(
                v-model="username"
                :class="{'is-invalid': usernameInvalid}"
              )
              small#userNameHelpBlock.form-text(
                class="text-muted"
                v-show="!usernameInvalid"
              ) {{ usernameInvalidMessage }}
              FormError(
                v-show="usernameInvalid"
              ) {{ usernameInvalidMessage }}
        .form-row
          .col
            .form-group
              label(for="first-name" required) First Name
              input#first-name.form-control(
                v-model="firstName"
                :class="{'is-invalid': firstNameInvalid}"
              )
              FormError(v-show="firstNameInvalid") First name is required.
          .col
            .form-group
              label(for="middle-name") Middle Name
              input#middle-name.form-control(v-model="middleName")
          .col
            .form-group
              label(for="last-name" required) Last Name
              input#last-name.form-control(
                v-model="lastName"
                :class="{'is-invalid': lastNameInvalid}"
              )
              FormError(v-show="lastNameInvalid") Last name is required.
        .form-row
          .col-6
            .form-group
              label(for="title") Title
              input#title.form-control(v-model="title")
        .form-row
          .col-6
            .form-group
              label(for="primary-email" required) Primary Email Address
              input#primary-email.form-control(
                v-model="email"
                :class="{'is-invalid': emailIfInvalid || emailInvalid}"
              )
              FormError(v-show="emailIfInvalid || emailInvalid") {{emailError}}
        .form-row
          .col-6
            .form-group
              label(for="alt-email1") Additional Email Address
              input#alt-email1.form-control(
                v-model="altEmail1"
                :class="{'is-invalid': altEmail1IfInvalid}")
              FormError(v-show="altEmail1IfInvalid") {{emailError}}
        .form-row
          .col-6
            .form-group
              label(for="alt-email2") Additional Email Address
              input#alt-email2.form-control(
                v-model="altEmail2"
                :class="{'is-invalid': altEmail2IfInvalid}")
              FormError(v-show="altEmail2IfInvalid") {{emailError}}
        .form-row
          .col-6
            .form-group
              label(for="work-phone") Work Phone
              input#work-phone.form-control(v-model="workPhone" :class="{'is-invalid': phoneFormatInvalid}")
              FormError(v-if="phoneFormatInvalid") {{ phoneError }}
        .form-row
          .col
            AgencySelector(
              :selected-agencies.sync="selectedAgencies"
              :selected-state.sync="selectedState"
              :state-required="true"
              :state-valid="!selectedStateInvalid"
              :agency-required="true"
              :agency-valid="!selectedAgenciesInvalid"
            )
        .form-row
          .col-6.form-group
            label(for="user-level" required) User Level
            select#user-level.form-control(
              v-model="userLevel"
              :class="{'is-invalid': userLevelInvalid}"
            )
              option(value="")
              option(v-for="key,value of levels" :key="value" :value="value") {{ key }}
            FormError(v-show="userLevelInvalid") User level is required.
        .form-row
          .col.form-group
            label User Role
            br
            CustomRadio(:model-value="true" v-model="sworn")  Sworn Personnel
            br
            CustomRadio(:model-value="false" v-model="sworn")  Non-Sworn Personnel (Civilian)
            br
        .form-row
          .col.form-group
            label Is this user a U.S. Immigration and Customs Enforcement (ICE) agent?
            br
            CustomRadio(:model-value="false" v-model="iceAgent")  No
            br
            CustomRadio(:model-value="true" v-model="iceAgent")  Yes
            br
        .form-row
          .col.form-group
            label(for="notes") Notes
            textarea#notes.form-control(v-model="notes")
        .form-row.mt-5
          .col.form-group
            button.btn.btn-primary.mr-2(type="submit") Submit
            button.btn.btn-light(@click.prevent="$router.push('/dashboard')") Cancel
</template>

<script>
import { currentGroup } from '@/lib/plugins/authorization'
import {
  mapTouched,
  mapRequired,
  mapRequiredIf,
  validationHelpers,
  phoneNumberValidators,
} from '@/lib/validation'
import { translateError } from '@/lib/utils'
import axios from '@/lib/axios'
import CustomRadio from '@/components/CustomRadio'
import CustomSelect from '@/components/CustomSelect'
import FormHeader from '@/components/FormHeader'
import states from '@/lib/states'
import stdmsg from '@/lib/stdmsg'
import AgencySelector from '@/components/AgencySelector'

export default {
  name: 'AddNewUser',
  data: function() {
    return {
      emailError: stdmsg.emailError,
      phoneError: stdmsg.phoneFormatError,
      selectedAgencies: [],
      selectedState: '',
      alertMessage: '',
      alertType: 'danger',
      group: currentGroup(),
      states: states,
      username: '',
      firstName: '',
      lastName: '',
      middleName: '',
      title: '',
      email: '',
      altEmail1: '',
      altEmail2: '',
      workPhone: '',
      userLevel: '',
      sworn: true,
      iceAgent: false,
      notes: '',
      usernameInvalidMessage:
        'Must be at least 8 characters and should not contain &, <, >, #, +, % or /.',
      ...mapTouched([
        'username',
        'firstName',
        'lastName',
        'email',
        'userLevel',
        'selectedAgencies',
        'selectedState',
        'altEmail1',
        'altEmail2',
        'workPhone',
      ]),
    }
  },
  computed: {
    ...mapRequired([
      'firstName',
      'lastName',
      'email',
      'userLevel',
      'selectedState',
    ]),
    ...phoneNumberValidators('workPhone'),
    ...mapRequiredIf(['email', 'altEmail1', 'altEmail2'], x =>
      /[^@]+@.+\..+/.test(x),
    ),
    usernameInvalid() {
      return (
        (this.username.length < 8 || /[&<>#+%/]/.test(this.username)) &&
        this.usernameTouched
      )
    },
    selectedAgenciesInvalid() {
      return this.selectedAgenciesTouched && this.selectedAgencies.length < 1
    },
    agencyStates() {
      return this.selectedAgencies.map(a => a.state)
    },
    agencyIds() {
      return this.selectedAgencies.map(a => a.id)
    },
    filteredStates() {
      const level = this.$store.state.account.userLevel
      if (level !== 'internal_admin') {
        return states.filter(st =>
          this.$store.state.account.states.includes(st.abbreviation),
        )
      } else {
        return states
      }
    },
    levels() {
      let levels = {
        basic: 'Basic User',
        agency_admin: 'Agency Administrator',
        state_admin: 'State Administrator',
        naddi_admin: 'NADDI Administrator',
        internal_admin: 'Internal Administrator',
      }
      if (this.group === 'agency_admin') {
        delete levels.agency_admin
        delete levels.state_admin
        delete levels.naddi_admin
        delete levels.internal_admin
      } else if (this.group === 'state_admin') {
        delete levels.state_admin
        delete levels.naddi_admin
        delete levels.internal_admin
      } else if (this.group === 'naddi_admin') {
        delete levels.state_admin
        delete levels.naddi_admin
        delete levels.internal_admin
      }
      return levels
    },
    anyInvalid() {
      return [
        'username',
        'firstName',
        'lastName',
        'email',
        'userLevel',
        'selectedAgencies',
        'emailIf',
        'altEmail1If',
        'altEmail2If',
        'workPhone',
      ].some(k => this[`${k}Invalid`])
    },
    formattedPhoneNumber() {
      return this.workPhone.replace(/[^\d]/g, '')
    },
  },
  methods: {
    ...validationHelpers(),
    submit() {
      this.alertMessage = ''
      this.markAllTouched()
      if (this.anyInvalid) {
        this.alertMessage = stdmsg.formError
        this.alertType = 'danger'
      } else {
        axios()
          .post('/api/admin/add_user', {
            username: this.username,
            email: this.email.toLowerCase(),
            altEmail1: this.altEmail1,
            altEmail2: this.altEmail2,
            firstName: this.firstName,
            lastName: this.lastName,
            middleName: this.middleName,
            states: this.agencyStates,
            agencies: this.agencyIds,
            userLevel: this.userLevel,
            sworn: this.sworn,
            workPhone: this.formattedPhoneNumber,
            title: this.title,
            iceAgent: this.iceAgent,
            notes: this.notes,
          })
          .then(() => {
            this.alertMessage =
              'User successfully added. They will receive a notification email with a temporary password.'
            this.alertType = 'success'
            this.clear()
            this.markAllNotTouched()
          })
          .catch(err => {
            this.alertMessage =
              (err.response &&
                err.response.data &&
                translateError(err.response.data.message)) ||
              err.message
            this.alertType = 'danger'
          })
      }
      window.scrollTo(0, 0)
    },
    clear() {
      this.username = ''
      this.firstName = ''
      this.lastName = ''
      this.middleName = ''
      this.title = ''
      this.email = ''
      this.altEmail1 = ''
      this.altEmail2 = ''
      this.workPhone = ''
      this.agencyName = ''
      this.userLevel = ''
      this.sworn = true
      this.notes = ''
      this.selectedAgencies = []
      this.selectedState = ''
      this.iceAgent = false
    },
  },
  components: {
    FormHeader,
    CustomRadio,
    CustomSelect,
    AgencySelector,
  },
}
</script>

<style lang="scss" scoped>
.form {
  padding-top: 1rem;
}
</style>
